import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import SubscriberTable from "../../components/tables/SubscriberTable";
import SubscriberViewModal from "../../components/modal/SubscriberViewModal";
import { api } from "../../utils/apiClient";
import LoadingShimmer from "../../utils/LoadingShimmer";
import { toast } from "react-toastify";
import { showDeleteConfirm } from "../../utils/deleteConfirm";

let searchTimeout;

const SubscribersPage = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEntries, setTotalEntries] = useState(0);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    // Sort
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");

    // Search
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Filters
    const [filterPlan, setFilterPlan] = useState("");
    const [filterPlatform, setFilterPlatform] = useState("");

    // View modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);

    useEffect(() => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 800);
        return () => clearTimeout(searchTimeout);
    }, [search]);

    const fetchSubscribers = async () => {
        try {
            setLoading(true);
            const response = await api.get("/subscribers", {
                params: {
                    page,
                    limit: entriesPerPage,
                    sort,
                    order,
                    search: debouncedSearch?.trim() || "",
                    ...(filterPlan && { type: filterPlan }),
                    ...(filterPlatform && { platform: filterPlatform }),
                },
            });
            setSubscribers(response.data.data || []);
            setTotalPages(response.data.pagination?.totalPages || 1);
            setTotalEntries(response.data.pagination?.total || 0);
        } catch (error) {
            console.error("Error fetching subscribers:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscribers();
    }, [page, entriesPerPage, sort, order, debouncedSearch, filterPlan, filterPlatform]);

    const handleView = (sub) => {
        setSelectedSubscriber(sub);
        setViewModalOpen(true);
    };

    const handleRefresh = () => {
        setPage(1);
        fetchSubscribers();
    };

    const handleDelete = (id) => {
        showDeleteConfirm(async () => {
            try {
                await api.delete(`/subscribers/${id}`);
                toast.success("Subscriber deleted successfully");
                fetchSubscribers();
            } catch {
                toast.error("Failed to delete subscriber");
            }
        });
    };

    return (
        <>
            <div className="page-header-row">
                <Breadcrumb title="Pro Subscribers" />
            </div>

            {loading ? (
                <LoadingShimmer
                    columns={9}
                    rows={6}
                    avatarColumnIndex={2}
                    actionColumnIndex={8}
                />
            ) : (
                <SubscriberTable
                    subscribers={subscribers}
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    totalEntries={totalEntries}
                    sort={sort}
                    order={order}
                    setSort={setSort}
                    setOrder={setOrder}
                    search={search}
                    setSearch={setSearch}
                    entriesPerPage={entriesPerPage}
                    setEntriesPerPage={setEntriesPerPage}
                    filterPlan={filterPlan}
                    setFilterPlan={(val) => { setFilterPlan(val); setPage(1); }}
                    filterPlatform={filterPlatform}
                    setFilterPlatform={(val) => { setFilterPlatform(val); setPage(1); }}
                    onRefresh={handleRefresh}
                    onView={handleView}
                    onDelete={handleDelete}
                />
            )}

            <SubscriberViewModal
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                data={selectedSubscriber}
            />
        </>
    );
};

export default SubscribersPage;
